import { useCallback, useEffect, useState } from "react";
import { Lock, RefreshCw, LogOut, Mail, CalendarHeart, Sparkles, Inbox as InboxIcon, Loader2, Phone } from "lucide-react";
import { toast } from "sonner";
import { inboxGet, getStoredPasscode, setStoredPasscode, clearStoredPasscode } from "@/lib/api";
import { LogoMark } from "@/components/Logo";

const TABS = [
  { id: "consultations", label: "Consultations", icon: CalendarHeart, testid: "inbox-tab-consultations" },
  { id: "enquiries", label: "Messages", icon: Mail, testid: "inbox-tab-enquiries" },
  { id: "quiz", label: "Quiz Matches", icon: Sparkles, testid: "inbox-tab-quiz" },
];

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" });

const Field = ({ label, value }) =>
  value ? (
    <p className="text-base text-espresso/90">
      <span className="font-semibold text-warmgray">{label}: </span>
      {value}
    </p>
  ) : null;

const ItemCard = ({ type, item }) => (
  <article
    data-testid={`inbox-item-${type}-${item.id}`}
    className="rounded-2xl border border-espresso/10 bg-white p-6 shadow-sm"
  >
    <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
      <h3 className="font-serif text-xl font-semibold text-espresso">{item.name}</h3>
      <time className="text-sm text-warmgray">{fmtDate(item.created_at)}</time>
    </div>
    <div className="space-y-1.5">
      <p className="text-base">
        <a href={`mailto:${item.email}`} className="font-semibold text-brand hover:underline">
          {item.email}
        </a>
        {item.phone && (
          <>
            {" · "}
            <a href={`tel:${item.phone}`} className="font-semibold text-brand hover:underline">
              <Phone className="inline h-4 w-4" /> {item.phone}
            </a>
          </>
        )}
      </p>
      {type === "consultations" && (
        <>
          <Field label="Preferred" value={`${item.date} — ${item.time_slot}`} />
          <Field label="Setting" value={item.setting} />
          <Field label="About their loved one" value={item.notes} />
        </>
      )}
      {type === "enquiries" && (
        <>
          <Field label="Loved one lives" value={item.senior_location} />
          <Field label="Message" value={item.message} />
        </>
      )}
      {type === "quiz" && (
        <>
          <Field label="Companion for" value={item.recipient} />
          <Field label="Lives" value={item.setting} />
          <Field label="Loves" value={item.interests?.join(", ")} />
          <Field label="Visit rhythm" value={item.frequency} />
        </>
      )}
    </div>
  </article>
);

export default function Inbox() {
  const [unlocked, setUnlocked] = useState(false);
  const [passcode, setPasscode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("consultations");
  const [data, setData] = useState({ consultations: [], enquiries: [], quiz: [] });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [consultations, enquiries, quiz] = await Promise.all([
        inboxGet("/consultations"),
        inboxGet("/enquiries"),
        inboxGet("/quiz-leads"),
      ]);
      setData({ consultations, enquiries, quiz });
      setUnlocked(true);
    } catch (e) {
      if (e?.response?.status === 401) {
        clearStoredPasscode();
        setUnlocked(false);
        setError("That passcode didn't work — please try again.");
      } else {
        toast.error("Couldn't load the inbox — please try again");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (getStoredPasscode()) load();
  }, [load]);

  const unlock = (e) => {
    e.preventDefault();
    setStoredPasscode(passcode.trim());
    load();
  };

  const lock = () => {
    clearStoredPasscode();
    setUnlocked(false);
    setPasscode("");
    setData({ consultations: [], enquiries: [], quiz: [] });
  };

  if (!unlocked) {
    return (
      <div className="mx-auto max-w-md px-4 py-24 sm:py-32" data-testid="inbox-page">
        <div className="rounded-3xl border border-espresso/10 bg-white p-8 text-center shadow-xl shadow-espresso/10 sm:p-10">
          <LogoMark className="mx-auto mb-5 h-16 w-16" />
          <h1 className="mb-2 font-serif text-2xl font-semibold text-espresso sm:text-3xl">Leads Inbox</h1>
          <p className="mb-7 text-base text-warmgray">
            This page is private. Enter the owner passcode to view enquiries, bookings and quiz matches.
          </p>
          <form onSubmit={unlock} className="space-y-4">
            <label htmlFor="inbox-passcode" className="sr-only">Owner passcode</label>
            <input
              id="inbox-passcode"
              data-testid="inbox-passcode-input"
              type="password"
              className="input-field text-center"
              placeholder="Owner passcode"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              required
            />
            {error && (
              <p data-testid="inbox-error" className="text-base font-semibold text-red-700" role="alert">
                {error}
              </p>
            )}
            <button type="submit" data-testid="inbox-unlock-button" disabled={loading} className="btn-primary w-full disabled:opacity-60">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Lock className="h-5 w-5" />}
              Unlock Inbox
            </button>
          </form>
        </div>
      </div>
    );
  }

  const items = data[tab];

  return (
    <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8" data-testid="inbox-page">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="eyebrow mb-2">Private</p>
          <h1 className="font-serif text-3xl font-semibold tracking-tight text-espresso sm:text-4xl" data-testid="inbox-heading">
            Leads Inbox
          </h1>
        </div>
        <div className="flex gap-3">
          <button data-testid="inbox-refresh-button" onClick={load} disabled={loading} className="btn-outline !px-5 !py-2.5 !text-base disabled:opacity-60">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <RefreshCw className="h-5 w-5" />} Refresh
          </button>
          <button data-testid="inbox-lock-button" onClick={lock} className="btn-outline !px-5 !py-2.5 !text-base">
            <LogOut className="h-5 w-5" /> Lock
          </button>
        </div>
      </div>

      <div className="mb-8 flex flex-wrap gap-2.5" role="tablist" aria-label="Lead types">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            data-testid={t.testid}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 rounded-full border-2 px-5 py-2.5 text-base font-semibold transition-all duration-200 ${
              tab === t.id
                ? "border-brand bg-brand text-white shadow-md shadow-brand/25"
                : "border-espresso/15 bg-white text-espresso hover:border-brand hover:text-brand"
            }`}
          >
            <t.icon className="h-4 w-4" /> {t.label}
            <span className={`rounded-full px-2 py-0.5 text-sm ${tab === t.id ? "bg-white/25" : "bg-sand"}`}>
              {data[t.id].length}
            </span>
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="rounded-3xl border border-espresso/10 bg-white p-12 text-center" data-testid="inbox-empty">
          <InboxIcon className="mx-auto mb-4 h-12 w-12 text-warmgray/50" />
          <p className="text-lg text-warmgray">Nothing here yet — new {TABS.find((t) => t.id === tab)?.label.toLowerCase()} will appear here.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="inbox-list">
          {items.map((item) => (
            <ItemCard key={item.id} type={tab} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}
